import { useState, type FormEvent } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { useLocation } from 'wouter';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Search, Sparkles, BookOpen, Upload, FileText, ClipboardPaste } from 'lucide-react';
import { api, ApiCallError } from '@/lib/api';
import { useCurrentAdmin } from '@/hooks/use-current-admin';
import { AdminShell } from '@/components/admin-shell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FieldError } from '@/components/ui/field-error';
import { toast } from '@/components/ui/sonner';

const KINDS = [
  { value: 'fiscal_law', label: 'Ley fiscal' },
  { value: 'set_manual', label: 'Manual SET' },
  { value: 'tax_regulation', label: 'Decreto/Regulación' },
  { value: 'product_help', label: 'FAQ producto' },
  { value: 'invoice_example', label: 'Ejemplo factura' },
  { value: 'conversation_log', label: 'Conversación' },
  { value: 'internal_doc', label: 'Doc interna' },
] as const;

type Kind = (typeof KINDS)[number]['value'];

interface SourceRow {
  id: string;
  kind: Kind;
  title: string;
  url: string | null;
  publishedAt: string | null;
  contentHash: string;
  createdAt: string;
  chunkCount: number;
}

interface RetrievedChunk {
  chunkId: string;
  sourceId: string;
  content: string;
  headings: string[] | null;
  score: number;
  distance: number;
}

export function RagPage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading: loadingMe } = useCurrentAdmin();
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SourceRow | null>(null);
  const [tab, setTab] = useState<'sources' | 'search'>('sources');

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/login');
    return null;
  }

  return (
    <AdminShell>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">Base de conocimiento (RAG)</h1>
          <p className="text-sm text-muted-foreground">
            Fuentes que el bot conversacional consulta para responder con datos fiscales reales
          </p>
        </div>
        {me.role === 'superadmin' && (
          <Button onClick={() => setShowCreate(true)}>
            <Plus />
            Nueva fuente
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)} className="mb-4">
        <TabsList>
          <TabsTrigger value="sources">
            <BookOpen />
            Fuentes
          </TabsTrigger>
          <TabsTrigger value="search">
            <Search />
            Probar búsqueda
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sources" className="mt-4">
          <SourcesTable
            isSuper={me.role === 'superadmin'}
            onDelete={setDeleteTarget}
          />
        </TabsContent>

        <TabsContent value="search" className="mt-4">
          <SearchTab />
        </TabsContent>
      </Tabs>

      <CreateSourceDialog open={showCreate} onOpenChange={setShowCreate} />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar fuente</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que querés eliminar "<strong>{deleteTarget?.title}</strong>"? Todos sus chunks
              y embeddings se borran. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={async () => {
                if (!deleteTarget) return;
                try {
                  await api(`/api/admin/rag/sources/${deleteTarget.id}`, { method: 'DELETE' });
                  toast.success('Fuente eliminada');
                  setDeleteTarget(null);
                } catch {
                  toast.error('No se pudo eliminar');
                }
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}

function SourcesTable({
  isSuper,
  onDelete,
}: {
  isSuper: boolean;
  onDelete: (s: SourceRow) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['rag-sources'],
    queryFn: () => api<{ items: SourceRow[] }>('/api/admin/rag/sources'),
  });

  return (
    <div className="rounded-lg border border-border bg-background shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Título</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead className="text-right">Chunks</TableHead>
            <TableHead>Agregada</TableHead>
            {isSuper && <TableHead></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                Cargando…
              </TableCell>
            </TableRow>
          )}
          {!isLoading && data?.items.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                No hay fuentes todavía. El bot va a responder sin contexto fiscal hasta que
                agregués alguna.
              </TableCell>
            </TableRow>
          )}
          {data?.items.map((s) => (
            <TableRow key={s.id}>
              <TableCell className="font-medium">
                <div>{s.title}</div>
                {s.url && (
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-primary hover:underline"
                  >
                    {s.url}
                  </a>
                )}
              </TableCell>
              <TableCell>
                <Badge variant="secondary">
                  {KINDS.find((k) => k.value === s.kind)?.label ?? s.kind}
                </Badge>
              </TableCell>
              <TableCell className="text-right font-mono">{s.chunkCount}</TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {new Date(s.createdAt).toLocaleDateString('es-PY')}
              </TableCell>
              {isSuper && (
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => onDelete(s)}>
                    <Trash2 />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function SearchTab() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<RetrievedChunk[]>([]);

  const mutation = useMutation({
    mutationFn: () =>
      api<{ chunks: RetrievedChunk[] }>('/api/admin/rag/search', {
        method: 'POST',
        body: JSON.stringify({ query, topK: 5 }),
      }),
    onSuccess: (r) => setResults(r.chunks),
    onError: () => toast.error('Búsqueda falló'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!query.trim()) return;
    mutation.mutate();
  }

  return (
    <div className="space-y-4">
      <form onSubmit={onSubmit} className="flex gap-2">
        <Input
          placeholder="¿Cómo se deduce el IVA crédito de servicios médicos?"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1"
        />
        <Button type="submit" disabled={mutation.isPending || !query.trim()}>
          <Sparkles />
          {mutation.isPending ? 'Buscando…' : 'Buscar'}
        </Button>
      </form>

      {results.length === 0 && !mutation.isPending && (
        <p className="text-muted-foreground text-sm py-8 text-center">
          Escribí una consulta para probar el retrieval semántico.
        </p>
      )}

      <div className="space-y-3">
        {results.map((r, idx) => (
          <div key={r.chunkId} className="rounded-lg border border-border bg-background p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Badge variant="default">#{idx + 1}</Badge>
                <span className="text-xs text-muted-foreground">
                  score: <span className="font-mono">{r.score.toFixed(3)}</span>
                </span>
              </div>
              {r.headings && r.headings.length > 0 && (
                <span className="text-xs text-muted-foreground">{r.headings.join(' › ')}</span>
              )}
            </div>
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{r.content}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreateSourceDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'paste' | 'upload'>('paste');
  const [kind, setKind] = useState<Kind>('fiscal_law');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [rawContent, setRawContent] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [issues, setIssues] = useState<Record<string, string[]>>({});

  function reset() {
    setMode('paste');
    setKind('fiscal_law');
    setTitle('');
    setUrl('');
    setRawContent('');
    setFile(null);
    setIssues({});
  }

  const pasteMutation = useMutation({
    mutationFn: () =>
      api<{ sourceId: string; chunksInserted: number; alreadyExists: boolean }>(
        '/api/admin/rag/sources',
        {
          method: 'POST',
          body: JSON.stringify({
            kind,
            title,
            rawContent,
            url: url || null,
          }),
        }
      ),
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('no file');
      const fd = new FormData();
      fd.append('kind', kind);
      fd.append('title', title);
      if (url) fd.append('url', url);
      fd.append('file', file);
      const res = await fetch('/api/admin/rag/sources/upload', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({ error: 'unknown' }))) as {
          error: string;
        };
        throw new ApiCallError(res.status, body);
      }
      return res.json() as Promise<{
        sourceId: string;
        chunksInserted: number;
        alreadyExists: boolean;
      }>;
    },
    onSuccess: handleSuccess,
    onError: handleError,
  });

  function handleSuccess(r: { chunksInserted: number; alreadyExists: boolean }) {
    qc.invalidateQueries({ queryKey: ['rag-sources'] });
    if (r.alreadyExists) {
      toast.info(`Ya existía esa fuente (${r.chunksInserted} chunks)`);
    } else {
      toast.success(`Fuente agregada — ${r.chunksInserted} chunks indexados`);
    }
    onOpenChange(false);
    reset();
  }

  function handleError(err: unknown) {
    if (err instanceof ApiCallError) {
      if (err.body.issues) {
        setIssues(err.body.issues);
      } else if (err.body.error === 'unsupported_mime') {
        toast.error('Formato no soportado. Aceptamos PDF, DOCX, TXT y MD.');
      } else if (err.body.error === 'extracted_text_too_short') {
        toast.error('El archivo no tiene suficiente texto para indexar.');
      } else if (err.body.error === 'extraction_failed') {
        toast.error('No se pudo extraer el texto del archivo.');
      } else {
        toast.error(`No se pudo agregar: ${err.body.error}`);
      }
    } else {
      toast.error('No se pudo agregar la fuente');
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setIssues({});
    if (mode === 'paste') pasteMutation.mutate();
    else uploadMutation.mutate();
  }

  const isPending = pasteMutation.isPending || uploadMutation.isPending;
  const canSubmit =
    title.trim().length > 0 && (mode === 'paste' ? rawContent.trim().length >= 20 : !!file);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nueva fuente de conocimiento</DialogTitle>
          <DialogDescription>
            Pegá texto o subí un archivo (PDF, DOCX, TXT, MD). Se chunkea automáticamente y se
            generan los embeddings.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={(v) => setMode(v as 'paste' | 'upload')}>
          <TabsList>
            <TabsTrigger value="paste">
              <ClipboardPaste />
              Pegar texto
            </TabsTrigger>
            <TabsTrigger value="upload">
              <Upload />
              Subir archivo
            </TabsTrigger>
          </TabsList>

          <form onSubmit={onSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label required>Tipo</Label>
                <Select value={kind} onValueChange={(v) => setKind(v as Kind)}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KINDS.map((k) => (
                      <SelectItem key={k.value} value={k.value}>
                        {k.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="url">URL (opcional)</Label>
                <Input
                  id="url"
                  placeholder="https://..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="title" required>
                Título
              </Label>
              <Input
                id="title"
                placeholder="Ley 6380 — Modernización y Simplificación Tributaria"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
              <FieldError error={issues.title} />
            </div>

            <TabsContent value="paste" className="space-y-1.5">
              <Label htmlFor="rawContent" required>
                Contenido completo
              </Label>
              <Textarea
                id="rawContent"
                placeholder="Pegá el texto del documento…"
                value={rawContent}
                onChange={(e) => setRawContent(e.target.value)}
                rows={12}
                minLength={20}
              />
              <p className="text-xs text-muted-foreground">
                Mín 20 caracteres. Markdown soportado (los encabezados `#` se usan como
                breadcrumb).
              </p>
              <FieldError error={issues.rawContent} />
            </TabsContent>

            <TabsContent value="upload" className="space-y-2">
              <Label required>Archivo (PDF, DOCX, TXT, MD — máx 25 MB)</Label>
              <input
                type="file"
                accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,.pdf,.docx,.txt,.md"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-accent file:px-4 file:py-2 file:text-sm file:font-medium file:text-accent-foreground hover:file:bg-accent/80"
              />
              {file && (
                <div className="rounded-md bg-muted/30 px-3 py-2 text-xs flex items-center gap-2">
                  <FileText className="size-4" />
                  <div>
                    <div className="font-medium">{file.name}</div>
                    <div className="text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB · {file.type || 'tipo desconocido'}
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isPending || !canSubmit}>
                {isPending ? 'Procesando…' : 'Agregar e indexar'}
              </Button>
            </DialogFooter>
          </form>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
